import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Mail, Share2 } from "lucide-react";
import stories from "@/content/pribehy.json";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { shareUrl } from "@/pages/Pribehy";

const MAILTO =
  "mailto:danielpekar@icloud.com?subject=" + encodeURIComponent("Můj příběh — Nebuď šmejd");

export default function PribehyDetail() {
  const { slug } = useParams();
  const story = stories.find((s) => s.slug === slug);

  useEffect(() => {
    if (story) {
      document.title = `${story.title} | Nebuď šmejd — Daniel Pekař`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", story.excerpt);
    }
    return () => { document.title = "Daniel Pekař – finanční poradce Ústí nad Labem"; };
  }, [story]);

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {!story ? (
            <div data-testid="pribeh-not-found">
              <h1 className="font-display text-4xl text-[#252525]">Tento příběh jsem nenašel</h1>
              <Link
                to="/pribehy"
                data-testid="pribeh-back-overview"
                className="inline-flex items-center gap-2 mt-8 text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Všechny příběhy
              </Link>
            </div>
          ) : (
            <article data-testid="pribeh-detail">
              <Link
                to="/pribehy"
                data-testid="pribeh-back-link"
                className="inline-flex items-center gap-2 text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Nebuď šmejd — všechny příběhy
              </Link>

              <p className="text-xs uppercase tracking-widest text-[#737373] mt-10">
                {new Date(story.date).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })}
                {" · "}Skutečný příběh, anonymizováno
              </p>
              <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.12] text-[#252525] mt-6">
                {story.title}
              </h1>

              <div className="mt-10 space-y-6">
                {story.body.map((paragraph, i) => (
                  <p key={i} className="text-base md:text-lg text-[#4A4A4A] leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="w-12 h-[1px] bg-[#B69A58] my-12" aria-hidden="true" />

              <section data-testid="pribeh-lesson">
                <h2 className="font-display text-2xl md:text-3xl text-[#252525]">Co si z toho odnést</h2>
                <ul className="mt-6 space-y-4">
                  {story.lesson.map((item, i) => (
                    <li key={i} className="flex gap-4">
                      <Check className="h-5 w-5 text-[#B69A58] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
                      <p className="text-base text-[#4A4A4A] leading-relaxed">{item}</p>
                    </li>
                  ))}
                </ul>
              </section>

              <div className="mt-14 border border-[#F0F0F0] p-8 md:p-10" data-testid="pribeh-cta">
                <h2 className="font-display text-2xl text-[#252525]">Stalo se vám něco podobného?</h2>
                <p className="text-base text-[#4A4A4A] leading-relaxed mt-3">
                  Napište mi svůj příběh e-mailem — zveřejníme ho, anonymně pokud budete chtít.
                  Nebo příběh pošlete dál někomu, komu by mohl pomoct.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <a
                    href={MAILTO}
                    data-testid="pribeh-mailto-button"
                    className="inline-flex items-center justify-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
                  >
                    <Mail className="h-4 w-4 text-[#B69A58]" strokeWidth={1.5} aria-hidden="true" />
                    Poslat svůj příběh
                  </a>
                  <a
                    href={shareUrl(story)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pribeh-share-button"
                    className="inline-flex items-center justify-center gap-2 border border-[#E5E5E5] text-[#252525] hover:border-[#252525] transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
                  >
                    <Share2 className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                    Poslat dál přes WhatsApp
                  </a>
                </div>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
